import { format, isValid, parse, parseISO } from 'date-fns';
import { Check, Edit, X } from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { updateLottery } from '@/api/Lottery';
import Button from '@/components/UI/Button';
import type { Lottery } from '@/types/Lottery';
import EditableField from './EditableField';

interface LotteryInformationProps {
  lottery: Lottery;
  onUpdate: (updatedLottery: Partial<Lottery>) => void;
}

const emailTemplateVariables = ['{{user_name}}', '{{lottery_name}}', '{{lottery_start_date}}', '{{lottery_end_date}}'];

const EditableTemplate: React.FC<{
  value: string;
  onSave: (name: string, value: string) => Promise<void>;
  fieldName: string;
  label: string;
}> = ({ value, onSave, fieldName, label }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const templateRef = useRef<HTMLTextAreaElement>(null);

  const handleVariableClick = (variable: string) => {
    if (!templateRef.current) return;

    const currentValue = editValue;
    const cursorPosition = templateRef.current.selectionStart || 0;
    const newValue = currentValue.slice(0, cursorPosition) + variable + currentValue.slice(cursorPosition);

    setEditValue(newValue);

    // Set cursor position after the inserted variable
    setTimeout(() => {
      if (templateRef.current) {
        const newCursorPosition = cursorPosition + variable.length;
        templateRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        templateRef.current.focus();
      }
    }, 0);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSave(fieldName, editValue);
      setIsEditing(false);
    } catch (error) {
      console.error(`Failed to save ${label}`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  return (
    <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
      <dt className='text-sm font-medium text-gray-500'>{label}</dt>
      <dd className='mt-1 flex items-start justify-between text-sm text-gray-900 sm:col-span-2 sm:mt-0'>
        {isEditing ? (
          <div className='flex w-full flex-col gap-2'>
            <textarea
              ref={templateRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-hidden focus:ring-1 focus:ring-blue-500 resize-none'
              rows={5}
              style={{ minHeight: '120px' }}
            />
            <div className='flex flex-wrap gap-2'>
              {emailTemplateVariables.map((variable) => (
                <button
                  type='button'
                  key={variable}
                  className='text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-mono py-1 px-2 rounded'
                  onClick={() => handleVariableClick(variable)}
                >
                  {variable}
                </button>
              ))}
            </div>
            <div className='flex items-center gap-1'>
              <Button btnStyle='primary' onClick={handleSave} disabled={isLoading} customClassName='p-2' icon={<Check size={16} />} />
              <Button btnStyle='secondary' onClick={handleCancel} customClassName='p-2' icon={<X size={16} />} />
            </div>
          </div>
        ) : (
          <div className='flex w-full items-start gap-2'>
            <div className='flex-1 w-8'>
              <div className='whitespace-pre-wrap text-sm text-gray-600 bg-gray-50 p-3 rounded border break-words overflow-hidden'>
                {value || 'No email template set'}
              </div>
            </div>
            <Button btnStyle='ghost' onClick={() => setIsEditing(true)} customClassName='p-1 shrink-0' icon={<Edit size={16} />} />
          </div>
        )}
      </dd>
    </div>
  );
};

const LotteryInformation: React.FC<LotteryInformationProps> = ({ lottery, onUpdate }) => {
  const _navigate = useNavigate();

  const handleSave = async (name: string, value: string | number | boolean) => {
    try {
      const updatedLottery = { [name]: value };
      const response = await updateLottery(lottery.id, updatedLottery);
      // Handle different response structures - the API might return { data: Lottery } or just Lottery
      const updatedData = (response.data as any).data || response.data;
      onUpdate(updatedData);
    } catch (error) {
      console.error('Failed to update lottery', error);
      throw error; // Re-throw to be caught by EditableField
    }
  };

  const handleDateSave = async (field: 'startDate' | 'endDate', value: string) => {
    try {
      // Parse the date string in the expected format
      const parsedDate = parse(value, 'MM/dd/yyyy', new Date());
      if (!isValid(parsedDate)) {
        throw new Error('Invalid date format. Please use MM/DD/YYYY');
      }
      const isoString = parsedDate.toISOString();
      const response = await updateLottery(lottery.id, { [field]: isoString });
      // Handle different response structures - the API might return { data: Lottery } or just Lottery
      const updatedData = (response.data as any).data || response.data;
      onUpdate(updatedData);
    } catch (error) {
      console.error('Failed to update lottery date', error);
      throw error;
    }
  };

  const nomineeFormLink = `${window.location.origin}/lottery/${lottery.id}/nominee`;

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MM/dd/yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <section aria-labelledby='lottery-information-title'>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='border-t border-gray-200 px-4 py-5 sm:p-0'>
          <dl className='sm:divide-y sm:divide-gray-200'>
            <EditableField name='name' label='Name' value={lottery.name || ''} onSave={handleSave} />
            <EditableField name='description' label='Description' value={lottery.description || ''} onSave={handleSave} type='richtext' />
            <EditableField
              name='termsAndConditions'
              label='Terms and Conditions'
              value={lottery.termsAndConditions || ''}
              onSave={handleSave}
              type='richtext'
            />
            <EditableField
              name='startDate'
              label='Open Date'
              value={formatDate(lottery.startDate)}
              onSave={(_name, value) => handleDateSave('startDate', value as string)}
              type='text'
            />
            <EditableField
              name='endDate'
              label='Closed Date'
              value={formatDate(lottery.endDate)}
              onSave={(_name, value) => handleDateSave('endDate', value as string)}
              type='text'
            />
            <EditableField
              name='maxParticipants'
              label='Max Participants'
              value={lottery.maxParticipants ?? 0}
              onSave={handleSave}
              type='number'
            />
            <EditableField name='totalWinners' label='Total Winners' value={lottery.totalWinners ?? 0} onSave={handleSave} type='number' />
            <EditableTemplate value={lottery.emailTemplate || ''} onSave={handleSave} fieldName='emailTemplate' label='Email Template' />
            <EditableTemplate value={lottery.smsTemplate || ''} onSave={handleSave} fieldName='smsTemplate' label='SMS Template' />
            <EditableTemplate
              value={lottery.notSelectedEmailTemplate || ''}
              onSave={handleSave}
              fieldName='notSelectedEmailTemplate'
              label='Not Selected Email Template'
            />
            <EditableTemplate
              value={lottery.notSelectedSmsTemplate || ''}
              onSave={handleSave}
              fieldName='notSelectedSmsTemplate'
              label='Not Selected SMS Template'
            />
            <EditableField
              name='confirmationMessage'
              label='Confirmation Message'
              value={lottery.confirmationMessage || ''}
              onSave={handleSave}
              type='textarea'
            />
            <EditableField name='isActive' label='Is Active' value={lottery.isActive || false} onSave={handleSave} type='checkbox' />
            <EditableField
              name='inActiveValidationErrorMessage'
              label='Inactive Validation Error Message'
              value={lottery.inActiveValidationErrorMessage || ''}
              onSave={handleSave}
              type='textarea'
            />
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6'>
              <dt className='text-sm font-medium text-gray-500'>Application Link</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0'>
                <a href={nomineeFormLink} target='_blank' rel='noopener noreferrer' className='text-indigo-600 hover:text-indigo-900'>
                  {nomineeFormLink}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
};

export default LotteryInformation;
